import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { format, addDays } from 'date-fns';

import FormGroup from '../../../../components/global/FormGroup';
import Button from '../../../../components/global/Button';
import { getErrorString } from '../../../../util/normalize-errors';
import Alert from '../../../../components/global/Alert';
import { sentryCaptureMessage } from '../../../../util/sentry';
import {
  formatNumber,
  formatPriceForDisplay,
} from '../../../../util/format-money';
import FaceMatchingIcon from '../../../../components/icons/FaceMatchingIcon';
import PhotosIcon from '../../../../components/icons/PhotosIcon';
import { DISCOUNT_MODE } from '../../../../util/promo-codes';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD } from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoFinderSubscriptionDetails/helpers';
import PriceDisplay from '../../../../components/global/PriceDisplay';
import { getPhotoManagerSubscriptionPrice } from '../helpers';

const propTypes = {
  className: PropTypes.string,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  photoManagerSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ),
  photoFinderSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ),
  supportText: PropTypes.node,
  accountPromoCode: PropTypes.shape({
    discount: PropTypes.shape({
      mode: PropTypes.oneOf(Object.values(DISCOUNT_MODE)),
      value: PropTypes.number,
    }),
  }),
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#969ea0',
      color: '#3c4c51',
      fontWeight: 800,
      fontFamily: 'Muli, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#969ea0',
        fontWeight: 800,
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const RegisterCheckoutCart = ({
  className,
  values,
  submitting,
  handleSubmit,
  handleChange,
  photoManagerSubscriptionItems,
  photoFinderSubscriptionItems,
  supportText,
  accountPromoCode,
}) => {
  const classes = classNames({
    [className]: !!className,
    'view-register-checkout-form': true,
  });
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState(
    `${values.first_name} ${values.last_name}`.trim(),
  );
  const [email, setEmail] = useState(values.email_address);
  const [loading, setLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');

  const photoManagerSubscriptionItem = photoManagerSubscriptionItems.find(
    subscriptionItem => subscriptionItem.id === values.plan_id,
  );
  const photoFinderSubscriptionItem = photoFinderSubscriptionItems.find(
    subscriptionItem => subscriptionItem.id === values.tier_id,
  );

  const photoManagerSubscriptionPrice = getPhotoManagerSubscriptionPrice({
    accountPromoCode,
    photoManagerSubscriptionItem,
  });

  const onPayment = async event => {
    event.preventDefault();

    if (loading || submitting) {
      return;
    }

    setLoading(true);
    setStripeError('');

    try {
      const { error, token } = await stripe.createToken(
        elements.getElement(CardElement),
        {
          name,
        },
      );

      if (error) {
        throw Error(error.message);
      }

      handleChange({
        target: {
          name: 'stripe_token',
          value: token,
        },
      });

      handleSubmit();
    } catch (error) {
      setStripeError(getErrorString(error));
      sentryCaptureMessage('PRO SIGNUP - CC Capture Error ', {
        level: 'error',
        extra: {
          error,
          email: values.email,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="view-register-photo-finder-upsell-checkout-wrapper">
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center mr-2">
            <div className="photo-manager-subscription-title-icon">
              <PhotosIcon color="white" size={14} />
            </div>
            <p className="text-xs text-dark mb-0">Photo Manager</p>
          </div>
          <div
            className="view-register-pill"
            data-for="discount-tooltip"
            data-placement="top"
            data-effect="solid"
            data-tip
          >
            <PriceDisplay
              title="Price"
              price={photoManagerSubscriptionPrice}
              oldPrice={photoManagerSubscriptionItem?.price.value}
              showDiscount
              animatedStrikethrough
            />
            {photoManagerSubscriptionItem && (
              <>
                /{' '}
                {photoManagerSubscriptionItem?.billing_period ===
                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                  ? 'mo'
                  : 'yr'}
              </>
            )}
            {!!values.promo_code && (
              <ReactTooltip id="discount-tooltip" className="tooltip">
                Your promo code has been applied!
              </ReactTooltip>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center mr-2">
            <div className="photo-finder-subscription-title-icon">
              <FaceMatchingIcon color="white" size={14} />
            </div>
            <p className="text-xs text-dark mb-0">Photo Finder</p>
          </div>

          <div className="view-register-pill">
            {photoFinderSubscriptionItem ? (
              <>
                <PriceDisplay
                  title="Price"
                  price={photoFinderSubscriptionItem.price?.value}
                  animatedStrikethrough
                />{' '}
                {photoFinderSubscriptionItem && (
                  <>
                    /{' '}
                    {photoFinderSubscriptionItem?.billing_period ===
                    PHOTO_FINDER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                      ? 'mo'
                      : 'yr'}
                  </>
                )}
              </>
            ) : (
              <>Free Trial</>
            )}
          </div>
        </div>
        <hr className="w-100 my-2" />
        <div className="d-flex justify-content-between align-items-center">
          <p className="text-extra-bold text-sm text-dark">
            {accountPromoCode?.trial_days
              ? `Charged on ${format(
                  addDays(Date.now(), accountPromoCode.trial_days),
                  'MMMM d, yyyy',
                )}`
              : "Today's charge"}
          </p>
          <p className="text-extra-bold text-sm text-primary">
            $
            {formatNumber(
              formatPriceForDisplay(
                (photoFinderSubscriptionItem?.price?.value ?? 0) +
                  photoManagerSubscriptionPrice,
                true,
              ),
            )}
          </p>
        </div>
      </div>
      <form className={classes} onSubmit={onPayment}>
        <fieldset className="view-register-checkout-fieldset">
          <FormGroup
            label="Name"
            labelSrOnly
            id="card-name"
            name="name"
            placeholder="Name on card"
            required
            value={name}
            onChange={event => {
              const { target } = event;
              const { value } = target;
              setName(value);
            }}
            disabled={submitting || loading}
          />
        </fieldset>
        <fieldset className="view-register-checkout-fieldset">
          <FormGroup
            label="Email"
            labelSrOnly
            id="card-email"
            name="email"
            placeholder="Email"
            type="email"
            required
            readOnly
            value={email}
            onChange={event => {
              const { target } = event;
              const { value } = target;
              setEmail(value);
            }}
            disabled={submitting || loading}
          />
        </fieldset>

        <fieldset className="view-register-checkout-fieldset view-register-checkout-fieldset-credit-card">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            disabled={submitting || loading}
          />
        </fieldset>

        {values.promo_code && (
          <fieldset className="view-register-checkout-fieldset view-register-promo-box p-2 mb-4">
            <FormGroup
              id="promo_code"
              name="promo_code"
              label="Promo Code"
              labelSrOnly
              placeholder="Have a promo code?"
              type="text"
              className="mb-1"
              readOnly
              defaultValue={values.promo_code}
            />

            <p className="mb-0 text-sm text-dark text-bold">
              Your promo code has been applied! You will be saving on a Photo
              Manager Subscription!
            </p>
          </fieldset>
        )}

        {stripeError && (
          <Alert type="danger" className="mt-3">
            {stripeError}
          </Alert>
        )}

        <div className="text-center mt-4">
          <Button
            id="pro-reg-btn-form-submit"
            className="pro-reg-btn-form-submit"
            type="submit"
            disabled={submitting || loading}
          >
            {submitting || loading ? 'Processing...' : 'Place the Order'}
          </Button>
        </div>
        {supportText}
      </form>
    </div>
  );
};

RegisterCheckoutCart.propTypes = propTypes;

export default RegisterCheckoutCart;
